import {
  Component,
  Inject,
  inject,
  PLATFORM_ID,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { SharedModule } from '../../../shared/shared.module';
import { MatDialog } from '@angular/material/dialog';
import { ForgotPasswordComponent } from '../forgot-password/forgot-password.component';
import { LocalStorageService } from '../../../core/service/localStorage/local-storage.service';
import { AuthenticationService } from '../../../core/service/auth/authentication.service';
import { PopupServiceService } from '../../../core/service/popup/popup-service.service';
import { LoaderService } from '../../../core/service/loader.service';
import moment from 'moment';
import { DomainConfigService } from '../../../core/service/domainConfig/domain-config.service';
import { ProfileService } from '../../../core/service/profile/profile.service';
import { InternalChatService } from '../../../core/service/internalChat/internal-chat.service';
import { TmsSocketService } from '../../../core/service/tms-socket/tms-socket.service';
import { TMSService } from '../../../core/service/tms/tms.service';
import { AwayStatusComponent } from '../../../shared/away-status/away-status.component';
import { TodayOverviewDialogComponent } from '../../../shared/today-overview-dialog/today-overview-dialog.component';
import { TodayOverviewService } from '../../../shared/today-overview/today-overview.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, SharedModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  readonly dialog = inject(MatDialog);
  private router = inject(Router);
  private localStorageService = inject(LocalStorageService);
  private fb = inject(FormBuilder);
  private auth = inject(AuthenticationService);
  readonly popup = inject(PopupServiceService);
  private profileService = inject(ProfileService);
  private domainConfigService = inject(DomainConfigService);
  private internalChatService = inject(InternalChatService);
  private todayOverviewService = inject(TodayOverviewService);
  private tmsSocketService = inject(TmsSocketService);
  private tmsService = inject(TMSService);
  // private startDayService = inject(StartDayService);

  brandLogo = './assets/images/logo.svg';
  loginForm!: FormGroup;
  isBrowser = false;
  hidePassword = true;
  public loader = inject(LoaderService);
  currentYear = moment().format('YYYY');

  // Domain-specific styling
  buttonBgColor = '#FF6525';
  buttonTextColor = '#ffffff';
  buttonDisabledBgColor = 'rgba(26, 27, 31, 0.12)';
  buttonDisabledTextColor = 'rgba(26, 27, 31, 0.38)';
  companyName = 'Crizac Ltd';
  userDetailsData: any = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    // Set domain-specific branding
    this.setupDomainBranding();

    // ✅ Correct place to initialize form
    if (this.isBrowser) {
      this.loginForm = this.fb.group({
        username: [
          '',
          [Validators.required, Validators.email],
        ],
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.maxLength(20),
          ],
        ],
        remember: [false],
      });

      if (this.localStorageService.getKey('access_token')) {
        this.router.navigate(['/dashboard/all-application']);
      }
    }
  }

  /**
   * Setup domain-specific branding (logo, button colors)
   */
  private setupDomainBranding(): void {
    this.brandLogo = this.domainConfigService.getLoginLogo();
    this.buttonBgColor = this.domainConfigService.getButtonBgColor();
    this.buttonTextColor = this.domainConfigService.getButtonTextColor();
    this.buttonDisabledBgColor = this.domainConfigService.getButtonDisabledBgColor();
    this.companyName = this.domainConfigService.getCompanyName();
  }

  onPasswordInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const trimmed = input.value.trim();
    this.loginForm.get('password')?.setValue(trimmed, { emitEvent: false });
  }

  onEmailInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Allow only letters, numbers, @, and .
    value = value.replace(/[^a-zA-Z0-9@.]/g, '');

    // Limit to 30 characters
    // if (value.length > 30) {
    //   value = value.substring(0, 30);
    // }

    input.value = value;

    // Update the reactive form control (use correct form name: loginForm)
    this.loginForm.get('username')?.setValue(value, { emitEvent: false });
  }

  onSubmit() {
    if (this.loginForm.valid) {
      const { username, password, remember } = this.loginForm.value;

      const payload = {
        email: username,
        password,
        currentVersion: '0.8.98',
        remember: remember ? 'yes' : 'no',
        security: null,
        newPortal: "true",
        source: "web",
      };
      this.loader.show();
      this.auth.login(payload).subscribe({
        next: (res: any) => {
          this.loader.hide();
          if (res?.data?.token) {
            // Regular login flow
            this.localStorageService.setIndexedDbKey('currentUser', res.data);
            this.localStorageService.setKey('access_token', res.data.token);
            this.localStorageService.setKey('refreshToken', res.data.refreshToken);
            this.localStorageService.setKey('roles_permissions', JSON.stringify(res.data?.roles));

            this.proceedAfterLogin(res, username);
          } else if (res?.data?.qrCode) {
            // QR Code flow
            this.navigateToAuthenticator({
              email: username,
              password,
              remember: remember ? 'yes' : 'no',
              qrCode: res.data.qrCode,
              secretKey: res.data.secretKey,
              source: "web"
            });
          } else if (res?.message == 'Invalid security code') {
            this.navigateToAuthenticator({ email: username, password, remember });
          } else {
            this.popup.successAndErrorDialog('error', 'Invalid credentials');
          }
        },
        error: (err) => {
          this.loader.hide();
          const message = err?.error?.message;
          if (message == 'Invalid security code') {
            this.navigateToAuthenticator({ email: username, password, remember });
          } else {
            const errorMsg = err?.error?.message || 'Login failed. Please try again.';
            this.popup.successAndErrorDialog('error', errorMsg);
          }
        },
      });
    }
  }

  private proceedAfterLogin(res: any, username: string): void {
    // Init internal chat socket immediately after login
    this.internalChatService.initSocket(res?.data?.token, res?.data?.id);

    // Init TMS socket immediately after login
    this.tmsSocketService.connect();

    if (res?.data?.id) {
      this.getUserDetails(res?.data?.id).subscribe({
        next: (userRes: any) => {
          this.userDetailsData = userRes?.data?.[0] || userRes?.data;
          if (!this.userDetailsData?.userDetails?.timezone) {
            this.auth.setTImezone();
          }
        },
        error: (err: any) => {
          console.error('Error fetching user details:', err);
        }
      });
    }

    // If previous day was not signed off, make user catch up before proceeding —
    // but only when there's actually something to report; otherwise skip straight
    // to start-day with a note that sign-off was missed.
    if (res.data.isPrevDaySignOff === false && username !== 'automation@crizac.com') {
      this.todayOverviewService.hasCatchUpSignOffData().subscribe(hasData => {
        if (hasData) {
          this.router.navigate(['/sign-off-wrapup'], { state: { isCatchUp: true } });
        } else {
          this.router.navigate(['/start-day'], { state: { missedSignOff: true } });
        }
      });
      return;
    }

    // If user has not punched in yet, send to start-day page
    if (res.data.isSignIn === false && username !== 'automation@crizac.com') {
      this.router.navigate(['/start-day']);
      return;
    }

    // Already signed in — mark day started and proceed
    this.tmsService.markDayStarted();

    const redirectUrl = this.auth.getRedirectUrl();
    if (redirectUrl) {
      this.router.navigateByUrl(redirectUrl);
    } else {
      this.router.navigate(['/dashboard/main-dashboard']);
    }

    // This user skips /start-day entirely on this login (already punched in),
    // so it's never given a chance to prompt for MPIN setup — do it here instead.
    if (res.data?.isMpinAdded === false) {
      const mpinDialogRef = this.dialog.open(AwayStatusComponent, {
        panelClass: 'custom-dialog-container',
        width: '400px',
        disableClose: true,
        data: { context: 'login' },
      });
      // Wait for the mandatory MPIN setup to close before showing the recap —
      // both are disableClose-free/true respectively and shouldn't stack.
      mpinDialogRef.afterClosed().subscribe(() => this._openTodayOverview());
    } else {
      this._openTodayOverview();
    }
  }

  // Compact, read-only recap of today's tasks/calendar — shown every login so
  // the user has a quick reminder of what's on their plate. Only opens when
  // the API actually has something to show.
  private _openTodayOverview(): void {
    this.todayOverviewService.hasTodayData().subscribe(hasData => {
      if (!hasData) return;
      this.dialog.open(TodayOverviewDialogComponent, {
        panelClass: 'custom-dialog-container',
        width: '1100px',
        disableClose: false,
      });
    });
  }

  private navigateToAuthenticator(state: {
    email: string;
    password: string;
    remember: string;
    qrCode?: string;
    secretKey?: string;
    source?: string;
  }) {
    this.router.navigate(['/authenticator'], { state });
  }

  openDialog() {
    this.dialog.open(ForgotPasswordComponent, {
      panelClass: 'custom-dialog-container',
      width: '440px',
      disableClose: true,
    });
  }

  getUserDetails(userId: string | number) {
    return this.profileService.getUserDetailsById(userId);
  }
}